import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, Loader2, PlusCircle, Power, Layers, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch } from '../lib/authFetch';

function BackendBUPage() {
    const [types, setTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedType, setSelectedType] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [editName, setEditName] = useState('');
    const [editPartAName, setEditPartAName] = useState('Part A');
    const [editPartBName, setEditPartBName] = useState('Part B');
    const [editValuesA, setEditValuesA] = useState([]);
    const [editValuesB, setEditValuesB] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [typeToDelete, setTypeToDelete] = useState(null);
    const [saving, setSaving] = useState(false);
    const [showNewTypeModal, setShowNewTypeModal] = useState(false);
    const [newTypeName, setNewTypeName] = useState('');
    const [newPartAName, setNewPartAName] = useState('Part A');
    const [newPartBName, setNewPartBName] = useState('Part B');
    const [expandedA, setExpandedA] = useState(true);
    const [expandedB, setExpandedB] = useState(true);

    // Fetch all types
    const fetchTypes = async () => {
        try {
            setLoading(true);
            const response = await authFetch('http://localhost:5000/api/bu-types');
            const data = await response.json();
            if (data.success) {
                setTypes(data.data);
                return data.data;
            }
            return [];
        } catch (error) {
            console.error('Error fetching types:', error);
            toast.error('Failed to fetch types');
            return [];
        } finally {
            setLoading(false);
        }
    };

    // Fetch type with values
    const fetchTypeWithValues = async (id) => {
        try {
            const response = await authFetch(`http://localhost:5000/api/bu-types/${id}`);
            const data = await response.json();
            if (data.success) {
                setSelectedType(data.data);
                setEditName(data.data.type_name);
                setEditPartAName(data.data.part_a_name || 'Part A');
                setEditPartBName(data.data.part_b_name || 'Part B');
                setEditValuesA(data.data.valuesA?.map(v => v.value_text) || []);
                setEditValuesB(data.data.valuesB?.map(v => v.value_text) || []);
            }
        } catch (error) {
            console.error('Error fetching type:', error);
            toast.error('Failed to fetch type details');
        }
    };

    // On mount: fetch types and auto-select active one
    useEffect(() => {
        (async () => {
            const allTypes = await fetchTypes();
            const active = allTypes.find(t => t.status === 'On');
            if (active) {
                fetchTypeWithValues(active.id);
            } else if (allTypes.length > 0) {
                // No active type — auto-activate the first one
                try {
                    await authFetch(`http://localhost:5000/api/bu-types/${allTypes[0].id}/toggle-status`, {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ status: 'On' })
                    });
                    const refreshed = await fetchTypes();
                    const newActive = refreshed.find(t => t.status === 'On');
                    if (newActive) fetchTypeWithValues(newActive.id);
                } catch (err) {
                    console.error('Error auto-activating type:', err);
                }
            }
        })();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Create new type
    const handleCreateType = async () => {
        if (!newTypeName.trim()) {
            toast.error('Type name is required');
            return;
        }

        try {
            setSaving(true);
            const response = await authFetch('http://localhost:5000/api/bu-types', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    type_name: newTypeName.trim(),
                    part_a_name: newPartAName.trim() || 'Part A',
                    part_b_name: newPartBName.trim() || 'Part B'
                })
            });
            const data = await response.json();
            if (data.success) {
                toast.success('Type created successfully');
                setShowNewTypeModal(false);
                setNewTypeName('');
                setNewPartAName('Part A');
                setNewPartBName('Part B');
                const refreshed = await fetchTypes();
                // If this is the first type, auto-activate it
                if (refreshed.length === 1) {
                    await authFetch(`http://localhost:5000/api/bu-types/${refreshed[0].id}/toggle-status`, {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ status: 'On' })
                    });
                    await fetchTypes();
                    fetchTypeWithValues(refreshed[0].id);
                }
            }
        } catch (error) {
            console.error('Error creating type:', error);
            toast.error('Failed to create type');
        } finally {
            setSaving(false);
        }
    };

    // Toggle status (only one can be On at a time)
    const handleToggleStatus = async (e, type) => {
        e.stopPropagation();
        const newStatus = type.status === 'On' ? 'Off' : 'On';
        try {
            const response = await authFetch(`http://localhost:5000/api/bu-types/${type.id}/toggle-status`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: newStatus })
            });
            const data = await response.json();
            if (!response.ok) {
                toast.error(data.error || 'Failed to toggle status');
                return;
            }
            if (data.success) {
                toast.success(`${type.type_name} set to ${newStatus}`);
                const refreshed = await fetchTypes();
                // Always show the active type on the right
                const active = refreshed.find(t => t.status === 'On');
                if (active) {
                    fetchTypeWithValues(active.id);
                    setEditMode(false);
                }
            }
        } catch (error) {
            console.error('Error toggling status:', error);
            toast.error('Failed to toggle status');
        }
    };

    // Select a type
    const handleSelectType = (type) => {
        fetchTypeWithValues(type.id);
        setEditMode(false);
    };

    // Save changes (name + part names + values)
    const handleSave = async () => {
        try {
            setSaving(true);

            await authFetch(`http://localhost:5000/api/bu-types/${selectedType.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    type_name: editName,
                    part_a_name: editPartAName,
                    part_b_name: editPartBName
                })
            });

            await authFetch(`http://localhost:5000/api/bu-types/${selectedType.id}/values`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ valuesA: editValuesA, valuesB: editValuesB })
            });

            toast.success('Saved successfully');
            setEditMode(false);
            fetchTypeWithValues(selectedType.id);
            fetchTypes();
        } catch (error) {
            console.error('Error saving:', error);
            toast.error('Failed to save');
        } finally {
            setSaving(false);
        }
    };

    // Delete type
    const handleDelete = async () => {
        try {
            setSaving(true);
            const response = await authFetch(`http://localhost:5000/api/bu-types/${typeToDelete.id}`, {
                method: 'DELETE'
            });
            const data = await response.json();
            if (data.success) {
                toast.success('Type deleted successfully');
                setShowDeleteModal(false);
                setTypeToDelete(null);
                const refreshed = await fetchTypes();
                // After deleting, always show the active type
                const active = refreshed.find(t => t.status === 'On');
                if (active) {
                    fetchTypeWithValues(active.id);
                } else if (refreshed.length > 0) {
                    // Auto-activate first remaining type
                    await authFetch(`http://localhost:5000/api/bu-types/${refreshed[0].id}/toggle-status`, {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ status: 'On' })
                    });
                    await fetchTypes();
                    fetchTypeWithValues(refreshed[0].id);
                } else {
                    setSelectedType(null);
                }
            }
        } catch (error) {
            console.error('Error deleting type:', error);
            toast.error('Failed to delete type');
        } finally {
            setSaving(false);
        }
    };

    // Cancel edit — reset state
    const handleCancelEdit = () => {
        setEditMode(false);
        setEditName(selectedType.type_name);
        setEditPartAName(selectedType.part_a_name || 'Part A');
        setEditPartBName(selectedType.part_b_name || 'Part B');
        setEditValuesA(selectedType.valuesA?.map(v => v.value_text) || []);
        setEditValuesB(selectedType.valuesB?.map(v => v.value_text) || []);
    };

    // ── Reusable collapsible values section ──
    const ValuesSection = ({ label, editLabel, onEditLabel, values, editVals, onAdd, onChange, onRemove, isEdit, color, expanded, onToggle }) => {
        const colorClasses = {
            blue: {
                header: 'from-blue-50 to-blue-100/50 border-blue-200',
                headerText: 'text-blue-800',
                badge: 'bg-blue-100 text-blue-700',
                addBtn: 'bg-blue-50 text-blue-600 hover:bg-blue-100',
                border: 'border-blue-100',
                input: 'focus:ring-blue-500',
                chevron: 'text-blue-500',
            },
            violet: {
                header: 'from-violet-50 to-violet-100/50 border-violet-200',
                headerText: 'text-violet-800',
                badge: 'bg-violet-100 text-violet-700',
                addBtn: 'bg-violet-50 text-violet-600 hover:bg-violet-100',
                border: 'border-violet-100',
                input: 'focus:ring-violet-500',
                chevron: 'text-violet-500',
            }
        };
        const cls = colorClasses[color] || colorClasses.blue;
        const count = isEdit ? editVals.length : values.length;

        return (
            <div className="border rounded-xl overflow-hidden">
                {/* Section header (clickable to expand/collapse) */}
                <div
                    className={`px-4 py-3 bg-gradient-to-r ${cls.header} flex items-center justify-between cursor-pointer select-none`}
                    onClick={onToggle}
                >
                    <div className="flex items-center gap-2">
                        {expanded
                            ? <ChevronDown className={`w-4 h-4 ${cls.chevron} transition-transform`} />
                            : <ChevronRight className={`w-4 h-4 ${cls.chevron} transition-transform`} />
                        }
                        <Layers className={`w-4 h-4 ${cls.headerText}`} />
                        {isEdit ? (
                            <input
                                type="text"
                                value={editLabel}
                                onChange={(e) => onEditLabel(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                className={`text-sm font-bold bg-white px-2 py-1 border border-slate-300 rounded-lg ${cls.input} w-48`}
                                placeholder="Section name"
                            />
                        ) : (
                            <h4 className={`text-sm font-bold ${cls.headerText}`}>{label}</h4>
                        )}
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${cls.badge}`}>
                            {count}
                        </span>
                    </div>
                    {isEdit && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onAdd(); }}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors ${cls.addBtn}`}
                        >
                            <PlusCircle className="w-3.5 h-3.5" /> Add Value
                        </button>
                    )}
                </div>

                {/* Collapsible content */}
                {expanded && (
                    <div className="p-3">
                        {isEdit ? (
                            editVals.length === 0 ? (
                                <div className="text-center py-6 text-gray-400 text-sm border-2 border-dashed border-gray-200 rounded-lg">
                                    No values. Click &quot;Add Value&quot; above.
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 gap-2">
                                    {editVals.map((value, index) => (
                                        <div key={index} className="flex items-center gap-2">
                                            <span className="text-gray-400 w-6 text-xs font-medium text-right shrink-0">{index + 1}.</span>
                                            <input
                                                type="text"
                                                value={value}
                                                onChange={(e) => onChange(index, e.target.value)}
                                                className={`flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent text-gray-900 bg-white text-sm ${cls.input}`}
                                                placeholder={`Value ${index + 1}`}
                                            />
                                            <button
                                                onClick={() => onRemove(index)}
                                                className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors shrink-0"
                                                title="Remove value"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )
                        ) : (
                            values.length === 0 ? (
                                <div className="text-center py-6 text-gray-400 text-sm">
                                    No values in this section.
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 gap-1.5">
                                    {values.map((item, index) => (
                                        <div
                                            key={item.id}
                                            className={`flex items-center gap-2 p-2.5 bg-gray-50 rounded-lg border ${cls.border}`}
                                        >
                                            <span className="text-gray-400 w-6 text-xs font-medium text-right shrink-0">{index + 1}.</span>
                                            <span className="text-gray-900 text-sm">{item.value_text || '-'}</span>
                                        </div>
                                    ))}
                                </div>
                            )
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="flex gap-6 h-[calc(100vh-150px)]">
            {/* Left Sidebar - Types List */}
            <div className="w-80 bg-white rounded-2xl shadow-sm border border-slate-200 flex flex-col">
                <div className="p-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-lg font-bold text-slate-900">Backend BU Types</h2>
                        <button
                            onClick={() => setShowNewTypeModal(true)}
                            className="p-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors"
                            title="Add New Type"
                        >
                            <Plus className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-2">
                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                        </div>
                    ) : types.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            No types found. Create one to get started.
                        </div>
                    ) : (
                        types.map(type => (
                            <div
                                key={type.id}
                                className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all mb-1 ${selectedType?.id === type.id
                                    ? 'bg-blue-100 border border-blue-300'
                                    : type.status === 'On'
                                        ? 'bg-green-50 border border-green-200'
                                        : 'hover:bg-gray-100 border border-transparent'
                                    }`}
                                onClick={() => handleSelectType(type)}
                            >
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                    <span className="font-semibold text-gray-900 truncate">{type.type_name}</span>
                                    {type.status === 'On' && (
                                        <span className="shrink-0 px-2 py-0.5 text-xs font-bold bg-green-500 text-white rounded-full">
                                            Active
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    <button
                                        onClick={(e) => handleToggleStatus(e, type)}
                                        className={`p-1.5 rounded transition-colors ${type.status === 'On'
                                            ? 'text-green-600 hover:bg-green-100'
                                            : 'text-gray-400 hover:bg-gray-200'
                                            }`}
                                        title={type.status === 'On' ? 'Active (cannot disable)' : 'Turn On'}
                                    >
                                        <Power className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setTypeToDelete(type);
                                            setShowDeleteModal(true);
                                        }}
                                        className="p-1.5 text-red-500 hover:bg-red-100 rounded transition-colors"
                                        title="Delete Type"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Right Panel - Type Details & Values (A & B) */}
            <div className="flex-1 bg-white rounded-2xl shadow-sm border border-slate-200 flex flex-col">
                {!selectedType ? (
                    <div className="flex-1 flex items-center justify-center text-gray-500">
                        {loading ? 'Loading...' : 'Select a type from the left to view or edit its values'}
                    </div>
                ) : (
                    <>
                        {/* Header */}
                        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                            <div className="flex-1">
                                {editMode ? (
                                    <input
                                        type="text"
                                        value={editName}
                                        onChange={(e) => setEditName(e.target.value)}
                                        className="text-xl font-bold text-gray-900 bg-white px-3 py-2 border border-slate-300 rounded-xl focus:ring-4 focus:ring-primary-100 w-full max-w-md"
                                        placeholder="Type name"
                                    />
                                ) : (
                                    <div className="flex items-center gap-3">
                                        <h2 className="text-xl font-bold text-gray-900">{selectedType.type_name}</h2>
                                        <span className={`px-3 py-1 text-xs font-bold rounded-full ${selectedType.status === 'On'
                                            ? 'bg-green-100 text-green-700 border border-green-300'
                                            : 'bg-gray-100 text-gray-500 border border-gray-300'
                                            }`}>
                                            {selectedType.status === 'On' ? '● Active' : '○ Inactive'}
                                        </span>
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                {editMode ? (
                                    <>
                                        <button
                                            onClick={handleCancelEdit}
                                            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
                                        >
                                            <X className="w-4 h-4" /> Cancel
                                        </button>
                                        <button
                                            onClick={handleSave}
                                            disabled={saving}
                                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                                        >
                                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                            Save
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        onClick={() => setEditMode(true)}
                                        className="px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors flex items-center gap-2 font-semibold"
                                    >
                                        <Edit2 className="w-4 h-4" /> Edit
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Two collapsible sections (A & B) */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-5">
                            <ValuesSection
                                label={selectedType.part_a_name || 'Part A'}
                                editLabel={editPartAName}
                                onEditLabel={setEditPartAName}
                                values={selectedType.valuesA || []}
                                editVals={editValuesA}
                                onAdd={() => setEditValuesA(prev => [...prev, ''])}
                                onChange={(idx, val) => setEditValuesA(prev => { const u = [...prev]; u[idx] = val; return u; })}
                                onRemove={(idx) => setEditValuesA(prev => prev.filter((_, i) => i !== idx))}
                                isEdit={editMode}
                                color="blue"
                                expanded={expandedA}
                                onToggle={() => setExpandedA(prev => !prev)}
                            />

                            <ValuesSection
                                label={selectedType.part_b_name || 'Part B'}
                                editLabel={editPartBName}
                                onEditLabel={setEditPartBName}
                                values={selectedType.valuesB || []}
                                editVals={editValuesB}
                                onAdd={() => setEditValuesB(prev => [...prev, ''])}
                                onChange={(idx, val) => setEditValuesB(prev => { const u = [...prev]; u[idx] = val; return u; })}
                                onRemove={(idx) => setEditValuesB(prev => prev.filter((_, i) => i !== idx))}
                                isEdit={editMode}
                                color="violet"
                                expanded={expandedB}
                                onToggle={() => setExpandedB(prev => !prev)}
                            />
                        </div>
                    </>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Type?</h3>
                        <p className="text-gray-600 mb-6">
                            Are you sure you want to delete &quot;{typeToDelete?.type_name}&quot; and all its values? This action cannot be undone.
                        </p>
                        <div className="flex space-x-3">
                            <button
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setTypeToDelete(null);
                                }}
                                className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={saving}
                                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* New Type Modal */}
            {showNewTypeModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Type</h3>

                        <div className="space-y-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Type Name *</label>
                                <input
                                    type="text"
                                    value={newTypeName}
                                    onChange={(e) => setNewTypeName(e.target.value)}
                                    placeholder="e.g., Type 1"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                                    autoFocus
                                    onKeyDown={(e) => e.key === 'Enter' && handleCreateType()}
                                />
                            </div>
                            <div className="flex gap-3">
                                <div className="flex-1">
                                    <label className="block text-sm font-medium text-blue-700 mb-1">Section A Name</label>
                                    <input
                                        type="text"
                                        value={newPartAName}
                                        onChange={(e) => setNewPartAName(e.target.value)}
                                        placeholder="Part A"
                                        className="w-full px-3 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-blue-50/50 text-sm"
                                    />
                                </div>
                                <div className="flex-1">
                                    <label className="block text-sm font-medium text-violet-700 mb-1">Section B Name</label>
                                    <input
                                        type="text"
                                        value={newPartBName}
                                        onChange={(e) => setNewPartBName(e.target.value)}
                                        placeholder="Part B"
                                        className="w-full px-3 py-2 border border-violet-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent text-gray-900 bg-violet-50/50 text-sm"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="flex space-x-3 mt-5">
                            <button
                                onClick={() => {
                                    setShowNewTypeModal(false);
                                    setNewTypeName('');
                                    setNewPartAName('Part A');
                                    setNewPartBName('Part B');
                                }}
                                className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateType}
                                disabled={saving || !newTypeName.trim()}
                                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                                Create
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default BackendBUPage;
